import React from "react";
import { Link } from "react-router-dom";
import { FormattedDate, FormattedNumber } from "react-intl";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Price, Dialog } from "@syos-music/common";

import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Typography,
  IconButton,
  Badge,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  WalletGiftcard as GiftIcon,
  Cart as OrderIcon,
  ChartBar as StatisticsIcon,
} from "mdi-material-ui";

import DeleteDiscountCode from "./DeleteDiscountCode";

function PricesList({ prices }) {
  return prices?.map((price, idx) => (
    <React.Fragment key={idx}>
      {Boolean(idx) && " / "}
      <Price {...price} />
    </React.Fragment>
  ));
}

const MUTATION = gql`
  mutation ($discountCodeId: ID!, $showInStats: Boolean!) {
    UpdateDiscountCode(id: $discountCodeId, showInStats: $showInStats) {
      discountCode {
        id
        showInStats
      }
    }
  }
`;

export default function DiscountCodesListItem({ discountCode, onDelete }) {
  const [showOrders, setShowOrders] = React.useState(false);

  const [updateDiscountCode, { loading: updating }] = useMutation(MUTATION);

  return (
    <>
      <ListItem dense divider>
        <ListItemText
          primary={
            <Stack spacing={1} direction="row" alignItems="center">
              <Typography component="span" color="primary">
                <strong>{discountCode.code}</strong>
              </Typography>

              <div>
                {discountCode.type === "fixed" && (
                  <PricesList prices={discountCode.values} />
                )}

                {discountCode.type === "percentage" && (
                  <FormattedNumber
                    value={discountCode.percentage}
                    // eslint-disable-next-line react/style-prop-object
                    style="percent"
                  />
                )}

                {discountCode.type === "gift" && (
                  <Price {...discountCode.value} />
                )}
              </div>
            </Stack>
          }
          primaryTypographyProps={{ component: "div" }}
          secondary={
            <small>
              <div>
                Crée le{" "}
                <FormattedDate
                  value={discountCode.createdAt}
                  year="numeric"
                  month="long"
                  day="2-digit"
                  hour="2-digit"
                  minute="2-digit"
                />
              </div>

              <div>
                Montants minimums (HT) :{" "}
                <PricesList prices={discountCode.minimumAmounts} />
              </div>

              {discountCode.usageLeft != null && (
                <div>Utilisations restantes : {discountCode.usageLeft}</div>
              )}

              {discountCode.startDate && (
                <div>
                  Date de début :{" "}
                  <FormattedDate
                    value={discountCode.startDate}
                    year="numeric"
                    month="long"
                    day="2-digit"
                    hour="2-digit"
                    minute="2-digit"
                  />
                </div>
              )}

              {discountCode.endDate && (
                <div>
                  Date de fin :{" "}
                  <FormattedDate
                    value={discountCode.endDate}
                    year="numeric"
                    month="long"
                    day="2-digit"
                    hour="2-digit"
                    minute="2-digit"
                  />
                </div>
              )}

              {Boolean(discountCode.constraints?.list.length) && (
                <>
                  <div>Contraintes :</div>

                  <Box ml={2}>
                    {discountCode.constraints.list.map((constraint, idx) => (
                      <React.Fragment key={idx}>
                        {Boolean(idx) && (
                          <div>
                            <strong>
                              {discountCode.constraints.combinator}
                            </strong>
                          </div>
                        )}

                        <div>
                          {constraint.type === "option" &&
                            [
                              constraint.option.field,
                              constraint.option.operator,
                              constraint.option.value,
                            ].join(" ")}

                          {constraint.type === "product" &&
                            [
                              "product",
                              constraint.product.operator,
                              constraint.product.product.name,
                            ].join(" ")}
                        </div>
                      </React.Fragment>
                    ))}
                  </Box>
                </>
              )}
            </small>
          }
          secondaryTypographyProps={{ component: "div" }}
        />

        <ListItemSecondaryAction>
          <Tooltip
            placement="top"
            title="Visible dans les statistiques Lapidou ?"
          >
            <span>
              <IconButton
                color={
                  updating
                    ? "primary"
                    : discountCode.showInStats
                    ? "secondary"
                    : "default"
                }
                onClick={() =>
                  updateDiscountCode({
                    variables: {
                      discountCodeId: discountCode.id,
                      showInStats: !discountCode.showInStats,
                    },
                  })
                }
                disabled={updating}
                size="large"
              >
                {updating ? <CircularProgress size={20} /> : <StatisticsIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            placement="top"
            title={discountCode.orders.count ? "Commandes" : "Aucune commande"}
          >
            <span>
              <IconButton
                onClick={() => setShowOrders(true)}
                disabled={!discountCode.orders.count}
                size="large"
              >
                <Badge badgeContent={discountCode.orders.count} color="primary">
                  <OrderIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>

          {discountCode.invoice && (
            <Tooltip
              placement="top"
              title={`Bon cadeau : ${discountCode.invoice.reference}`}
            >
              <IconButton
                component="a"
                href={discountCode.invoice.file.url}
                target={"_blank"}
                rel={"noopener noreferrer"}
                size="large"
              >
                <GiftIcon />
              </IconButton>
            </Tooltip>
          )}

          <DeleteDiscountCode discountCode={discountCode} onDeleted={onDelete}>
            {({ open }) => (
              <Tooltip placement="top" title="Supprimer le code de réduction">
                <IconButton onClick={open} size="large">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </DeleteDiscountCode>
        </ListItemSecondaryAction>
      </ListItem>

      <Dialog open={showOrders} onClose={() => setShowOrders(false)}>
        <Dialog.Title color="primary">
          Commande utilisant le code de réduction{" "}
          <code>{discountCode.code}</code>
        </Dialog.Title>

        <Dialog.Content>
          <List>
            {discountCode.orders.edges.map(({ cursor, node }) => (
              <ListItem
                key={cursor}
                dense
                button
                component={Link}
                to={`/orders/${node.id}`}
              >
                <ListItemText primary={node.user.profile.fullName} />

                {node.paymentDate && (
                  <ListItemText
                    primary={
                      <>
                        Paiment :{" "}
                        <FormattedDate
                          value={node.paymentDate}
                          year="numeric"
                          month="2-digit"
                          day="2-digit"
                          hour="2-digit"
                          minute="2-digit"
                        />
                      </>
                    }
                    primaryTypographyProps={{
                      color: "textSecondary",
                      align: "right",
                    }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Dialog.Content>
      </Dialog>
    </>
  );
}

DiscountCodesListItem.fragments = {
  discountCode: gql`
    fragment DiscountCodesListItem_discountCode on DiscountCode {
      id
      createdAt
      code
      type

      minimumAmounts {
        currency
        value
      }
      usageLeft
      startDate
      endDate
      showInStats

      ... on DiscountCode_Fixed {
        values {
          currency
          value
        }
        constraints {
          ...DiscountCodesListItem_discountCodeContraints
        }
      }

      ... on DiscountCode_Percentage {
        percentage
        constraints {
          ...DiscountCodesListItem_discountCodeContraints
        }
      }

      ... on DiscountCode_Gift {
        value {
          currency
          value
        }
        invoice {
          id
          reference

          file {
            id
            url
          }
        }
      }

      orders(first: 99999) {
        count

        edges {
          cursor
          node {
            id
            paymentDate

            user {
              id
              profile {
                fullName
              }
            }
          }
        }
      }

      ...DeleteDiscountCode_discountCode
    }

    fragment DiscountCodesListItem_discountCodeContraints on DiscountCodeConstraints {
      combinator
      list {
        type
        product {
          operator
          product {
            id
            name
          }
        }
        option {
          operator
          field
          value
        }
      }
    }

    ${DeleteDiscountCode.fragments.discountCode}
  `,
};
